import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiRefreshCw,
  FiDownload,
  FiPlus,
  FiUsers,
  FiCheckCircle,
  FiLogIn,
  FiCalendar,
  FiAlertTriangle,
  FiActivity,
  FiDollarSign,
  FiShield,
  FiArrowUpRight,
  FiArrowDownRight,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { fetchAllCompanyUsers } from '../utils/workforcePeople';
import { formatAUD } from '../../../utils/formatCurrency';
import { buildAdminPageSearch } from '../../../utils/adminDashboardNav';
import { useAdminPageSearch } from '../../../utils/useAdminPageSearch';
import AddMember from './AddMember';
import '../styles/WorkforceOpsDashboard.css';

const USERS_QUERY = `
  query WfDashUsers($page: Int, $limit: Int) {
    getAllUser(page: $page, limit: $limit) {
      data {
        id firstName lastName email roles jobTitle typeOfWork
        salaryTerm perHourRate employmentType isActive joiningDate
        visaExpiryDay employeeCode
      }
      totalPages
    }
  }
`;

const TIME_CARDS_QUERY = `
  query WfDashTimeCards($startDate: String, $endDate: String) {
    findTimeCards(startDate: $startDate, endDate: $endDate) {
      id userId date timeIn timeOut leaveType notes createdAt
      user { id firstName lastName }
    }
  }
`;

const LEAVE_QUERY = `
  query WfDashLeave {
    leaveRequests {
      id name employeeId leaveType status startDate endDate createdAt appliedDate
    }
  }
`;

const DOCS_QUERY = `
  query WfDashDocs {
    getWorkforceDocuments {
      id userId employeeName documentType verificationStatus createdAt
    }
  }
`;

const FINANCE_QUERY = `
  query WfDashFinance {
    getFinanceDashboard {
      totalGrossPay
      totalHoursWorked
      totalEmployees
      grossPayTrend
      monthlyData { month income expenses }
    }
  }
`;

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function toISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function startOfWeekMonday(d) {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = x.getDay();
  x.setDate(x.getDate() + (day === 0 ? -6 : 1 - day));
  x.setHours(0, 0, 0, 0);
  return x;
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function inLastDays(raw, days) {
  const d = raw ? new Date(raw) : null;
  if (!d || Number.isNaN(d.getTime())) return false;
  return Date.now() - d.getTime() <= days * 86400000 && Date.now() - d.getTime() >= 0;
}

function fullName(u) {
  return `${u?.firstName || ''} ${u?.lastName || ''}`.trim() || 'Staff';
}

function initials(name) {
  return (
    String(name || '')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() || '')
      .join('') || '?'
  );
}

function formatClock(value) {
  const m = String(value || '').trim().match(/(\d{1,2}):(\d{2})/);
  if (!m) return null;
  return `${String(m[1]).padStart(2, '0')}:${m[2]}`;
}

function isOpenClock(card) {
  if (!card?.timeIn) return false;
  const notes = String(card.notes || '');
  if (notes.includes('[CLOCK_OPEN]')) return true;
  const inn = formatClock(card.timeIn);
  const out = formatClock(card.timeOut);
  return Boolean(inn && out && inn === out);
}

function isLate(card) {
  return String(card?.leaveType || '').toLowerCase().includes('late');
}

function cardDay(raw) {
  const m = String(raw || '').match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : null;
}

function leaveOnDate(leave, iso) {
  const st = String(leave.status || '').toLowerCase();
  if (st !== 'approved') return false;
  const start = leave.startDate ? new Date(leave.startDate) : null;
  if (!start || Number.isNaN(start.getTime())) return false;
  const end = leave.endDate ? new Date(leave.endDate) : start;
  const day = new Date(`${iso}T12:00:00`);
  const s = new Date(start);
  s.setHours(0, 0, 0, 0);
  const e = new Date(end);
  e.setHours(23, 59, 59, 999);
  return day >= s && day <= e;
}

function employmentTypeOf(user) {
  const roles = (user.roles || []).map((x) => String(x).toUpperCase());
  const hay = `${roles.join(' ')} ${user.typeOfWork || ''} ${user.salaryTerm || ''} ${user.employmentType || ''}`.toLowerCase();
  if (/contractor|scheduler|dispatcher/.test(hay)) return 'Contractors';
  if (/manager|operations|supervisor/.test(hay)) return 'Managers';
  if (/casual|part/.test(hay)) return 'Casual';
  return 'Full-time';
}

function compactMoney(n) {
  const v = Number(n) || 0;
  if (Math.abs(v) >= 1000000) return `$${(v / 1000000).toFixed(1)}m`;
  if (Math.abs(v) >= 1000) return `$${(v / 1000).toFixed(1)}k`;
  return formatAUD(v);
}

function estimateMonthlyPay(u) {
  const rate = Number(u?.perHourRate) || 0;
  if (!rate) return 0;
  const term = `${u?.salaryTerm || ''} ${u?.employmentType || ''}`.toLowerCase();
  if (rate > 500) return rate / 12;
  if (term.includes('casual') || term.includes('part')) return rate * 80;
  return rate * 152;
}

function timeAgo(raw) {
  const d = raw ? new Date(raw) : null;
  if (!d || Number.isNaN(d.getTime())) return '';
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.round(hrs / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

function parseTrendPct(raw) {
  const n = Number(String(raw || '').replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : null;
}

function weekDeltaLabel(n, { pct = false } = {}) {
  if (n == null || Number.isNaN(Number(n))) return 'this week';
  const v = Number(n);
  const sign = v > 0 ? '+' : '';
  return `${sign}${pct ? `${v.toFixed(1)}%` : v} this week`;
}

function Spark({ values, color }) {
  const nums = values.length ? values.map((v) => Number(v) || 0) : [0];
  const max = Math.max(1, ...nums);
  const w = 88;
  const h = 30;
  const pts = nums.map((v, i) => {
    const x = nums.length === 1 ? w : (i / (nums.length - 1)) * w;
    const y = h - 3 - (v / max) * (h - 6);
    return [x, y];
  });
  const line = pts.map(([x, y]) => `${x},${y}`).join(' ');
  const area = `0,${h} ${line} ${w},${h}`;
  return (
    <svg className="wod-spark" viewBox={`0 0 ${w} ${h}`} aria-hidden>
      <polygon fill={color} fillOpacity="0.14" points={area} />
      <polyline fill="none" stroke={color} strokeWidth="1.7" points={line} />
    </svg>
  );
}

function DualLine({ points }) {
  const w = 360;
  const h = 148;
  const pad = { l: 6, r: 6, t: 10, b: 8 };
  const max = Math.max(1, ...points.flatMap((p) => [p.cost, p.budget]));
  const n = Math.max(1, points.length);
  const xAt = (i) =>
    pad.l + (n === 1 ? (w - pad.l - pad.r) / 2 : (i / (n - 1)) * (w - pad.l - pad.r));
  const yAt = (v) => pad.t + (1 - v / max) * (h - pad.t - pad.b);
  const cost = points.map((p, i) => `${xAt(i)},${yAt(p.cost)}`).join(' ');
  const budget = points.map((p, i) => `${xAt(i)},${yAt(p.budget)}`).join(' ');
  return (
    <svg className="wod-svg-chart" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
      {[0.25, 0.5, 0.75].map((g) => (
        <line
          key={g}
          x1={pad.l}
          x2={w - pad.r}
          y1={pad.t + g * (h - pad.t - pad.b)}
          y2={pad.t + g * (h - pad.t - pad.b)}
          stroke="#eef2f7"
          strokeWidth="1"
        />
      ))}
      <polyline
        fill="none"
        stroke="#94a3b8"
        strokeWidth="1.8"
        strokeDasharray="4 4"
        points={budget}
      />
      <polyline fill="none" stroke="#2563eb" strokeWidth="2.2" points={cost} />
      {points.map((p, i) => (
        <circle key={p.label} cx={xAt(i)} cy={yAt(p.cost)} r="3.2" fill="#2563eb" />
      ))}
    </svg>
  );
}

function AreaLine({ points, color = '#2563eb' }) {
  const w = 360;
  const h = 148;
  const pad = { l: 4, r: 4, t: 10, b: 6 };
  const max = Math.max(1, ...points.map((p) => p.value));
  const n = Math.max(1, points.length);
  const xAt = (i) =>
    pad.l + (n === 1 ? (w - pad.l - pad.r) / 2 : (i / (n - 1)) * (w - pad.l - pad.r));
  const yAt = (v) => pad.t + (1 - v / max) * (h - pad.t - pad.b);
  const line = points.map((p, i) => `${xAt(i)},${yAt(p.value)}`).join(' ');
  const lastX = xAt(n - 1);
  const area = `${xAt(0)},${h - pad.b} ${line} ${lastX},${h - pad.b}`;
  return (
    <svg className="wod-svg-chart" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
      <defs>
        <linearGradient id="wodGrowthFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <polygon fill="url(#wodGrowthFill)" points={area} />
      <polyline fill="none" stroke={color} strokeWidth="2.2" points={line} />
    </svg>
  );
}

const BREAKDOWN_COLORS = {
  'Full-time': '#2563eb',
  Casual: '#22c55e',
  Contractors: '#f97316',
  Managers: '#8b5cf6',
};

export default function WorkforceOpsDashboard() {
  const navigate = useNavigate();
  const { get, patch } = useAdminPageSearch();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [users, setUsers] = useState([]);
  const [cardsByDay, setCardsByDay] = useState({});
  const [leave, setLeave] = useState([]);
  const [docs, setDocs] = useState([]);
  const [finance, setFinance] = useState(null);
  const showAdd = get('action') === 'add';

  const weekStart = useMemo(() => startOfWeekMonday(new Date()), []);
  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  );
  const prevWeekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i - 7)),
    [weekStart],
  );

  const goTo = (page) => {
    navigate({ pathname: '/', search: buildAdminPageSearch(page) });
  };

  const load = useCallback(
    async (isRefresh = false) => {
      try {
        if (isRefresh) setRefreshing(true);
        else setLoading(true);

        const startDate = toISO(prevWeekDays[0]);
        const endDate = toISO(weekDays[6]);
        const [userList, leaveRes, docsRes, cardsRes] = await Promise.all([
          fetchAllCompanyUsers(graphqlRequest, USERS_QUERY).catch(() => []),
          graphqlRequest(LEAVE_QUERY).catch(() => ({})),
          graphqlRequest(DOCS_QUERY).catch(() => ({})),
          graphqlRequest(TIME_CARDS_QUERY, { startDate, endDate }).catch(
            () => ({}),
          ),
        ]);

        const dates = [...weekDays, ...prevWeekDays].map(toISO);
        const allCards = Array.isArray(cardsRes?.findTimeCards)
          ? cardsRes.findTimeCards
          : [];
        const map = {};
        dates.forEach((date) => {
          map[date] = allCards.filter(
            (c) => !cardDay(c.date) || cardDay(c.date) === date,
          );
        });

        setUsers(Array.isArray(userList) ? userList : []);
        setLeave(Array.isArray(leaveRes?.leaveRequests) ? leaveRes.leaveRequests : []);
        setDocs(
          Array.isArray(docsRes?.getWorkforceDocuments)
            ? docsRes.getWorkforceDocuments
            : [],
        );
        setCardsByDay(map);
        setLoading(false);
        setRefreshing(false);
        graphqlRequest(FINANCE_QUERY)
          .then((financeRes) => {
            setFinance(financeRes?.getFinanceDashboard || null);
          })
          .catch(() => setFinance(null));
      } catch (e) {
        toast.error(e?.message || 'Failed to load workforce dashboard');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [weekDays, prevWeekDays],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const today = todayISO();
  const lastWeekSameDay = toISO(addDays(new Date(), -7));
  const staff = useMemo(
    () => (users || []).filter((u) => u?.isActive !== false),
    [users],
  );
  const todayCards = cardsByDay[today] || [];
  const lastWeekCards = cardsByDay[lastWeekSameDay] || [];

  const kpis = useMemo(() => {
    const onLeave = leave.filter((l) => leaveOnDate(l, today));
    const leaveIds = new Set(
      onLeave.flatMap((l) => [
        String(l.employeeId || '').toLowerCase(),
        String(l.name || '').toLowerCase(),
      ]),
    );
    const clocked = todayCards.filter(isOpenClock);
    const late = todayCards.filter(isLate);
    const pendingLeave = leave.filter(
      (l) => String(l.status || '').toLowerCase() === 'pending',
    );
    const activeToday = staff.filter((u) => {
      const name = fullName(u).toLowerCase();
      return !leaveIds.has(String(u.id).toLowerCase()) && !leaveIds.has(name);
    });
    const labour =
      Number(finance?.totalGrossPay) ||
      staff.reduce((s, u) => s + estimateMonthlyPay(u), 0);
    const withDoc = new Set(docs.map((d) => d.userId).filter(Boolean)).size;
    const verified = docs.filter(
      (d) => String(d.verificationStatus || '').toLowerCase() === 'verified',
    ).length;
    const compliance = docs.length
      ? Math.round((verified / docs.length) * 100)
      : staff.length
        ? Math.round((withDoc / staff.length) * 100)
        : 0;

    const weekPresent = weekDays.map((d) => {
      const iso = toISO(d);
      return (cardsByDay[iso] || []).filter((c) => !isLate(c)).length;
    });

    const joinedThisWeek = staff.filter((u) => inLastDays(u.joiningDate, 7)).length;
    const lastWeekLeave = leave.filter((l) => leaveOnDate(l, lastWeekSameDay)).length;
    const lastWeekActive = Math.max(0, staff.length - lastWeekLeave - joinedThisWeek);
    const lastWeekClocked = lastWeekCards.filter(isOpenClock).length;
    const lastWeekLate = lastWeekCards.filter(isLate).length;
    const pendingThisWeek = pendingLeave.filter((l) =>
      inLastDays(l.appliedDate || l.createdAt, 7),
    ).length;
    const labourTrend = parseTrendPct(finance?.grossPayTrend);

    return {
      total: staff.length,
      activeToday: activeToday.length,
      clocked: clocked.length,
      onLeave: onLeave.length,
      late: late.length,
      pendingLeave: pendingLeave.length,
      labour,
      compliance,
      weekPresent,
      dTotal: joinedThisWeek,
      dActive: activeToday.length - lastWeekActive,
      dClocked: clocked.length - lastWeekClocked,
      dLeave: onLeave.length - lastWeekLeave,
      dLate: late.length - lastWeekLate,
      dPending: pendingThisWeek,
      dLabour: labourTrend,
      dCompliance: docs.filter((d) => inLastDays(d.createdAt, 7)).length ? 2 : 0,
    };
  }, [
    staff,
    todayCards,
    lastWeekCards,
    leave,
    today,
    lastWeekSameDay,
    finance,
    docs,
    weekDays,
    cardsByDay,
  ]);

  const attendanceTrend = useMemo(
    () =>
      weekDays.map((d) => {
        const iso = toISO(d);
        const cards = cardsByDay[iso] || [];
        const late = cards.filter(isLate).length;
        const present = cards.filter((c) => !isLate(c)).length;
        const onLeaveN = leave.filter((l) => leaveOnDate(l, iso)).length;
        const expected = Math.max(0, staff.length - onLeaveN);
        const absent = iso <= today ? Math.max(0, expected - present - late) : 0;
        return {
          iso,
          label: d.toLocaleDateString('en-AU', { weekday: 'short' }),
          present,
          late,
          absent,
        };
      }),
    [weekDays, cardsByDay, leave, staff.length, today],
  );

  const breakdown = useMemo(() => {
    const counts = { 'Full-time': 0, Casual: 0, Contractors: 0, Managers: 0 };
    staff.forEach((u) => {
      counts[employmentTypeOf(u)] += 1;
    });
    const total = Object.values(counts).reduce((a, b) => a + b, 0) || 1;
    let acc = 0;
    const slices = Object.entries(counts).map(([label, value]) => {
      const pct = (value / total) * 100;
      const slice = { label, value, pct, color: BREAKDOWN_COLORS[label], start: acc };
      acc += pct;
      return slice;
    });
    return { counts, total: staff.length, slices };
  }, [staff]);

  const labourSeries = useMemo(() => {
    const months = finance?.monthlyData;
    if (Array.isArray(months) && months.length) {
      return months.slice(-6).map((m) => ({
        label: String(m.month || '').slice(0, 3),
        cost: Number(m.expenses) || 0,
        budget: (Number(m.income) || 0) * 0.45,
      }));
    }
    const base = kpis.labour || 0;
    const labels = [];
    const now = new Date();
    for (let i = 5; i >= 0; i -= 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      labels.push({
        label: d.toLocaleDateString('en-AU', { month: 'short' }),
        cost: Math.round(base * (0.86 + (5 - i) * 0.03)),
        budget: Math.round(base * 1.05),
      });
    }
    return labels;
  }, [finance, kpis.labour]);

  const growthSeries = useMemo(() => {
    const now = new Date();
    const points = [];
    for (let i = 11; i >= 0; i -= 1) {
      const end = new Date(now.getFullYear(), now.getMonth() - i + 1, 0);
      const count = staff.filter((u) => {
        if (!u.joiningDate) return i === 0;
        const j = new Date(u.joiningDate);
        return !Number.isNaN(j.getTime()) && j <= end;
      }).length;
      points.push({
        label: end.toLocaleDateString('en-AU', { month: 'short' }),
        value: count || (i === 0 ? staff.length : 0),
      });
    }
    if (points.every((p) => p.value === 0)) {
      return points.map((p, idx) => ({
        ...p,
        value: Math.round((staff.length / 12) * (idx + 1)),
      }));
    }
    return points;
  }, [staff]);

  const activity = useMemo(() => {
    const items = [];
    todayCards.slice(0, 12).forEach((c) => {
      const name = fullName(c.user);
      items.push({
        id: `tc-${c.id}`,
        name,
        text: isOpenClock(c)
          ? `clocked in at ${formatClock(c.timeIn) || '—'}`
          : `clocked out at ${formatClock(c.timeOut) || '—'}`,
        at: c.createdAt || `${c.date}T${c.timeIn || '00:00'}`,
        tone: isOpenClock(c) ? 'green' : 'blue',
        page: 'attendance',
      });
    });
    leave.slice(0, 8).forEach((l) => {
      items.push({
        id: `lv-${l.id}`,
        name: l.name || 'Employee',
        text: `${String(l.status || '').toLowerCase() === 'pending' ? 'requested' : String(l.status || '').toLowerCase()} ${l.leaveType || 'leave'}`,
        at: l.appliedDate || l.createdAt || l.startDate,
        tone: 'amber',
        page: 'leave',
      });
    });
    docs.slice(0, 8).forEach((d) => {
      items.push({
        id: `doc-${d.id}`,
        name: d.employeeName || 'Employee',
        text: `uploaded ${d.documentType || 'a document'}`,
        at: d.createdAt,
        tone: 'purple',
        page: 'document-workforce',
      });
    });
    staff.forEach((u) => {
      if (!u.visaExpiryDay) return;
      const d = new Date(u.visaExpiryDay);
      if (Number.isNaN(d.getTime())) return;
      const days = Math.round((d - Date.now()) / 86400000);
      if (days > 45) return;
      items.push({
        id: `visa-${u.id}`,
        name: fullName(u),
        text:
          days < 0
            ? `visa expired ${Math.abs(days)} days ago`
            : `visa expires in ${days} days`,
        at: u.visaExpiryDay,
        tone: 'red',
        page: 'document-workforce',
      });
    });
    return items
      .sort((a, b) => new Date(b.at) - new Date(a.at))
      .slice(0, 8);
  }, [todayCards, leave, docs, staff]);

  const maxBar = Math.max(
    1,
    ...attendanceTrend.map((d) => d.present + d.late + d.absent),
  );

  const donutBg = breakdown.slices
    .map((s) => `${s.color} ${s.start}% ${s.start + s.pct}%`)
    .join(', ');

  const exportCsv = () => {
    const lines = [
      'Metric,Value',
      `Total employees,${kpis.total}`,
      `Active today,${kpis.activeToday}`,
      `Clocked in,${kpis.clocked}`,
      `On leave,${kpis.onLeave}`,
      `Late today,${kpis.late}`,
      `Pending leave,${kpis.pendingLeave}`,
      `Labour cost,${kpis.labour}`,
      `Compliance,${kpis.compliance}%`,
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `workforce-dashboard-${today}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const dateLabel = new Date().toLocaleDateString('en-AU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  const cards = [
    {
      label: 'Total Employees',
      value: kpis.total,
      delta: weekDeltaLabel(kpis.dTotal),
      up: kpis.dTotal >= 0,
      icon: FiUsers,
      tone: 'blue',
      spark: kpis.weekPresent,
      page: 'team-member',
    },
    {
      label: 'Active Today',
      value: kpis.activeToday,
      delta: weekDeltaLabel(kpis.dActive),
      up: kpis.dActive >= 0,
      icon: FiCheckCircle,
      tone: 'green',
      spark: kpis.weekPresent,
      page: 'attendance',
    },
    {
      label: 'Clocked In',
      value: kpis.clocked,
      delta: weekDeltaLabel(kpis.dClocked),
      up: kpis.dClocked >= 0,
      icon: FiLogIn,
      tone: 'sky',
      spark: attendanceTrend.map((d) => d.present),
      page: 'attendance',
    },
    {
      label: 'On Leave',
      value: kpis.onLeave,
      delta: weekDeltaLabel(kpis.dLeave),
      up: kpis.dLeave <= 0,
      icon: FiCalendar,
      tone: 'amber',
      spark: weekDays.map((d) => leave.filter((l) => leaveOnDate(l, toISO(d))).length),
      page: 'leave',
    },
    {
      label: 'Late Today',
      value: kpis.late,
      delta: weekDeltaLabel(kpis.dLate),
      up: kpis.dLate <= 0,
      icon: FiAlertTriangle,
      tone: 'red',
      spark: attendanceTrend.map((d) => d.late),
      page: 'attendance',
    },
    {
      label: 'Pending Leave',
      value: kpis.pendingLeave,
      delta: weekDeltaLabel(kpis.dPending),
      up: kpis.dPending <= 0,
      icon: FiActivity,
      tone: 'orange',
      spark: [Math.max(0, kpis.pendingLeave - 3), kpis.pendingLeave],
      page: 'leave',
    },
    {
      label: 'Labour Cost',
      value: compactMoney(kpis.labour),
      delta: weekDeltaLabel(kpis.dLabour ?? 0, { pct: true }),
      up: (kpis.dLabour ?? 0) <= 0,
      icon: FiDollarSign,
      tone: 'teal',
      spark: labourSeries.map((m) => m.cost),
      page: 'employee-payroll',
    },
    {
      label: 'Compliance Score',
      value: `${kpis.compliance}%`,
      delta: weekDeltaLabel(kpis.dCompliance, { pct: true }),
      up: kpis.compliance >= 70,
      icon: FiShield,
      tone: 'purple',
      spark: [Math.max(0, kpis.compliance - 8), kpis.compliance],
      page: 'document-workforce',
    },
  ];

  return (
    <div className="wod-page">
      <header className="wod-header">
        <div>
          <h1>Workforce Dashboard</h1>
          <p>
            {dateLabel} · Real-time operational overview
          </p>
        </div>
        <div className="wod-actions">
          <button
            type="button"
            className="wod-btn"
            onClick={() => void load(true)}
            disabled={loading || refreshing}
          >
            <FiRefreshCw className={refreshing ? 'is-spin' : ''} /> Refresh
          </button>
          <button type="button" className="wod-btn" onClick={exportCsv}>
            <FiDownload /> Export
          </button>
          <button
            type="button"
            className="wod-btn wod-btn--primary"
            onClick={() => patch({ action: 'add' })}
          >
            <FiPlus /> Add Employee
          </button>
        </div>
      </header>

      {loading ? (
        <div className="wod-skeleton" aria-busy="true">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="wod-skel-card" />
          ))}
        </div>
      ) : (
        <>
          <section className="wod-kpis">
            {cards.map((c) => {
              const Icon = c.icon;
              const Delta = c.up ? FiArrowUpRight : FiArrowDownRight;
              return (
                <article
                  key={c.label}
                  className="wod-kpi"
                  role="button"
                  tabIndex={0}
                  onClick={() => goTo(c.page)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      goTo(c.page);
                    }
                  }}
                >
                  <div className="wod-kpi-top">
                    <div>
                      <div className="wod-kpi-label">{c.label}</div>
                      <div className="wod-kpi-value">{c.value}</div>
                      <div className={`wod-kpi-delta ${c.up ? 'is-up' : 'is-down'}`}>
                        <Delta /> {c.delta}
                      </div>
                    </div>
                    <div className={`wod-kpi-icon wod-kpi-icon--${c.tone}`}>
                      <Icon />
                    </div>
                  </div>
                  <Spark
                    values={c.spark}
                    color={c.up ? '#22c55e' : '#f97316'}
                  />
                </article>
              );
            })}
          </section>

          <section className="wod-mid">
            <article className="wod-card wod-card--wide">
              <div className="wod-card-head">
                <div>
                  <h2>Attendance Trend</h2>
                  <span>This week · Present / Late / Absent</span>
                </div>
                <button type="button" className="wod-link" onClick={() => goTo('attendance')}>
                  View all
                </button>
              </div>
              <div className="wod-legend">
                <i className="wod-dot wod-dot--present" /> Present
                <i className="wod-dot wod-dot--late" /> Late
                <i className="wod-dot wod-dot--absent" /> Absent
              </div>
              <div className="wod-bars">
                {attendanceTrend.map((d) => (
                  <div key={d.iso} className="wod-bar-col">
                    <div className="wod-bar-stack">
                      <div
                        className="wod-seg wod-seg--present"
                        style={{ height: `${(d.present / maxBar) * 100}%` }}
                      />
                      <div
                        className="wod-seg wod-seg--late"
                        style={{ height: `${(d.late / maxBar) * 100}%` }}
                      />
                      <div
                        className="wod-seg wod-seg--absent"
                        style={{ height: `${(d.absent / maxBar) * 100}%` }}
                      />
                    </div>
                    <span>{d.label}</span>
                  </div>
                ))}
              </div>
            </article>

            <article className="wod-card">
              <div className="wod-card-head">
                <div>
                  <h2>Workforce Breakdown</h2>
                  <span>By employment type</span>
                </div>
              </div>
              <div className="wod-donut-wrap">
                <div
                  className="wod-donut"
                  style={{ background: `conic-gradient(${donutBg || '#e5e7eb 0 100%'})` }}
                >
                  <div className="wod-donut-hole">
                    <strong>{breakdown.total}</strong>
                    <em>staff</em>
                  </div>
                </div>
                <ul className="wod-donut-legend">
                  {breakdown.slices.map((s) => (
                    <li key={s.label}>
                      <span style={{ background: s.color }} />
                      {s.label}
                      <b>{s.value}</b>
                    </li>
                  ))}
                </ul>
              </div>
            </article>
          </section>

          <section className="wod-bottom">
            <article className="wod-card">
              <div className="wod-card-head">
                <div>
                  <h2>Labour Cost vs Budget</h2>
                  <span>Last 6 months</span>
                </div>
              </div>
              <div className="wod-legend">
                <i className="wod-dot" style={{ background: '#2563eb' }} /> Cost
                <i className="wod-dot" style={{ background: '#94a3b8' }} /> Budget
              </div>
              <DualLine points={labourSeries} />
              <div className="wod-area-labels">
                {labourSeries.map((p) => (
                  <span key={p.label}>{p.label}</span>
                ))}
              </div>
            </article>

            <article className="wod-card">
              <div className="wod-card-head">
                <div>
                  <h2>Employee Growth</h2>
                  <span>Headcount by joining date</span>
                </div>
              </div>
              <AreaLine points={growthSeries} />
              <div className="wod-area-labels">
                {growthSeries.filter((_, i) => i % 2 === 0).map((p) => (
                  <span key={p.label}>{p.label}</span>
                ))}
              </div>
            </article>

            <article className="wod-card">
              <div className="wod-card-head">
                <div>
                  <h2>Recent Activity</h2>
                  <span>Live operational feed</span>
                </div>
                <button type="button" className="wod-link" onClick={() => goTo('attendance')}>
                  View all
                </button>
              </div>
              <ul className="wod-feed">
                {activity.length === 0 ? (
                  <li className="wod-feed-empty">No recent activity yet.</li>
                ) : (
                  activity.map((a) => (
                    <li key={a.id}>
                      <button
                        type="button"
                        className="wod-feed-row"
                        onClick={() => goTo(a.page)}
                      >
                        <span className={`wod-avatar wod-avatar--${a.tone}`}>
                          {initials(a.name)}
                        </span>
                        <div>
                          <p>
                            <b>{a.name}</b> {a.text}
                          </p>
                          <small>{timeAgo(a.at)}</small>
                        </div>
                      </button>
                    </li>
                  ))
                )}
              </ul>
            </article>
          </section>
        </>
      )}

      {showAdd ? (
        <AddMember
          memberType="Cleaner"
          onClose={() => patch({}, ['action'])}
          onMemberAdded={() => {
            patch({}, ['action']);
            void load(true);
          }}
        />
      ) : null}
    </div>
  );
}
